
import React, { useState } from 'react';
import { EnhancedRoomMatch, OrganizedRoom, RoomMatchDetails } from "../types";
import { parseLineItemNum, compareLineItems } from "../utils";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { X, ChevronRight, GripVertical, RefreshCw } from "lucide-react";
import { ComparisonFilters } from "@/hooks/useComparisonState";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import {
    DndContext,
    pointerWithin,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    DragStartEvent,
    DragEndEvent,
    useDndMonitor,
    useDroppable,
} from '@dnd-kit/core';
import {
    sortableKeyboardCoordinates,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface RoomMatchesTabProps {
    matches: EnhancedRoomMatch[];
    filters: ComparisonFilters;
    onApprove: (id: number) => void;
    onReject: (id: number) => void;
    onUpdateMatch: (params: { matchId: number, insurerRoomIds: number[], contractorRoomIds: number[] }) => void;
    onCreateMatch: (params: { insurerRoomIds: number[], contractorRoomIds: number[] }) => void;
    unmatchedInsurerRooms: OrganizedRoom[];
    unmatchedContractorRooms: OrganizedRoom[];
    selectedRoomIds?: Set<number>;
    onToggleRoomSelection?: (id: number) => void;
    onClearRoomSelection?: () => void;
    onRematchRoom: (roomMatchId: number) => void;
}

export const RoomMatchesTab: React.FC<RoomMatchesTabProps> = ({
    matches,
    filters,
    onApprove,
    onReject,
    onUpdateMatch,
    onCreateMatch,
    unmatchedInsurerRooms,
    unmatchedContractorRooms,
    selectedRoomIds,
    onToggleRoomSelection,
    onClearRoomSelection,
    onRematchRoom
}) => {
    const [activeId, setActiveId] = useState<number | null>(null);
    const [isMatchesOpen, setIsMatchesOpen] = useState(true);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleBulkMove = (targetMatchId: number | 'new', roomIds: number[]) => {
        const selectedInsurerIds: number[] = [];
        const selectedContractorIds: number[] = [];

        roomIds.forEach(id => {
            const isInsurer = unmatchedInsurerRooms.some(r => r.id === id);
            if (isInsurer) selectedInsurerIds.push(id);
            else selectedContractorIds.push(id);
        });

        if (targetMatchId === 'new') {
            onCreateMatch({
                insurerRoomIds: selectedInsurerIds,
                contractorRoomIds: selectedContractorIds
            });
        } else {
            const match = matches.find(m => m.id === targetMatchId);
            if (!match) return;

            const iIds = [...(match.insurerDetails?.map(r => r.room.id) || [])];
            const cIds = [...(match.contractorDetails?.map(r => r.room.id) || [])];

            // Add new IDs if not present
            selectedInsurerIds.forEach(id => { if (!iIds.includes(id)) iIds.push(id); });
            selectedContractorIds.forEach(id => { if (!cIds.includes(id)) cIds.push(id); });

            onUpdateMatch({
                matchId: match.id,
                insurerRoomIds: iIds,
                contractorRoomIds: cIds
            });
        }
        if (onClearRoomSelection) onClearRoomSelection();
    };

    // --- Helpers to find room location ---
    type RoomLocation =
        | { type: 'match', match: EnhancedRoomMatch, side: 'insurer' | 'contractor', room: OrganizedRoom }
        | { type: 'unmatched', side: 'insurer' | 'contractor', room: OrganizedRoom };

    const findRoomLocation = (roomId: number): RoomLocation | null => {
        // Check Matches
        for (const m of matches) {
            const iRoomDetail = m.insurerDetails?.find(r => r.room.id === roomId);
            if (iRoomDetail) {
                return {
                    type: 'match',
                    match: m,
                    side: 'insurer',
                    room: {
                        ...iRoomDetail.room,
                        items: iRoomDetail.items,
                        totalRCV: iRoomDetail.total,
                        type: 'insurer',
                        matchId: m.id
                    } as OrganizedRoom
                };
            }

            const cRoomDetail = m.contractorDetails?.find(r => r.room.id === roomId);
            if (cRoomDetail) {
                return {
                    type: 'match',
                    match: m,
                    side: 'contractor',
                    room: {
                        ...cRoomDetail.room,
                        items: cRoomDetail.items,
                        totalRCV: cRoomDetail.total,
                        type: 'contractor',
                        matchId: m.id
                    } as OrganizedRoom
                };
            }
        }

        // Check Unmatched
        const uInsurer = unmatchedInsurerRooms.find(r => r.id === roomId);
        if (uInsurer) return { type: 'unmatched', side: 'insurer', room: uInsurer };

        const uContractor = unmatchedContractorRooms.find(r => r.id === roomId);
        if (uContractor) return { type: 'unmatched', side: 'contractor', room: uContractor };

        return null;
    };

    const handleUnmatchRoom = (match: EnhancedRoomMatch, roomId: number, side: 'insurer' | 'contractor') => {
        const iIds = match.insurerDetails?.map(r => r.room.id) || [];
        const cIds = match.contractorDetails?.map(r => r.room.id) || [];

        if (side === 'insurer') {
            onUpdateMatch({ matchId: match.id, insurerRoomIds: iIds.filter(id => id !== roomId), contractorRoomIds: cIds });
        } else {
            onUpdateMatch({ matchId: match.id, insurerRoomIds: iIds, contractorRoomIds: cIds.filter(id => id !== roomId) });
        }
    };

    const addToMatch = (match: EnhancedRoomMatch, sourceId: number, sourceSide: 'insurer' | 'contractor') => {
        const iIds = [...(match.insurerDetails?.map(r => r.room.id) || [])];
        const cIds = [...(match.contractorDetails?.map(r => r.room.id) || [])];

        // Add source to match if not already present
        if (sourceSide === 'insurer' && !iIds.includes(sourceId)) iIds.push(sourceId);
        else if (sourceSide === 'contractor' && !cIds.includes(sourceId)) cIds.push(sourceId);

        onUpdateMatch({ matchId: match.id, insurerRoomIds: iIds, contractorRoomIds: cIds });
    };

    function handleDragStart(event: DragStartEvent) {
        setActiveId(Number(event.active.id));
    }

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveId(null);

        if (!over) return;

        const sourceId = Number(active.id);
        const targetId = over.id; // string or number

        const sourceLoc = findRoomLocation(sourceId);
        if (!sourceLoc) return;

        // Case: Dragging to "Unmatched Zone"
        if (targetId === 'unmatched-zone') {
            if (sourceLoc.type === 'match') {
                handleUnmatchRoom(sourceLoc.match, sourceId, sourceLoc.side);
            }
            return;
        }

        // Case: Dragging to Match Group (Directly on header/container)
        // Check for string prefix 'match-group-'
        if (typeof targetId === 'string' && targetId.startsWith('match-group-')) {
            const matchId = Number(targetId.replace('match-group-', ''));
            const targetMatch = matches.find(m => m.id === matchId);

            if (targetMatch) {
                // 1. Unmatched -> Match
                if (sourceLoc.type === 'unmatched') {
                    addToMatch(targetMatch, sourceId, sourceLoc.side);
                }
                // 2. Match -> Match (Move between groups)
                else if (sourceLoc.type === 'match') {
                    if (sourceLoc.match.id !== matchId) {
                        addToMatch(targetMatch, sourceId, sourceLoc.side);
                    }
                }
            }
            return;
        }

        // Case: Dragging to a specific Room (Match or Unmatched)
        const targetRoomId = Number(targetId);
        if (isNaN(targetRoomId)) return; // Not a room ID drop

        const targetLoc = findRoomLocation(targetRoomId);
        if (!targetLoc) return;

        // 1. Unmatched -> Match
        if (sourceLoc.type === 'unmatched' && targetLoc.type === 'match') {
            addToMatch(targetLoc.match, sourceId, sourceLoc.side);
        }

        // 2. Unmatched -> Unmatched (Create new match)
        else if (
            sourceLoc.type === 'unmatched' &&
            targetLoc.type === 'unmatched' &&
            sourceLoc.side !== targetLoc.side
        ) {
            onCreateMatch({
                insurerRoomIds: sourceLoc.side === 'insurer' ? [sourceId] : [targetRoomId],
                contractorRoomIds: sourceLoc.side === 'contractor' ? [sourceId] : [targetRoomId]
            });
        }
        // 3. Match -> Match (Move room between groups)
        else if (sourceLoc.type === 'match' && targetLoc.type === 'match') {
            // Prevent dropping on itself (same match group)
            if (sourceLoc.match.id === targetLoc.match.id) return;

            // Add to Target Match (Backend will auto-remove from Source Match)
            addToMatch(targetLoc.match, sourceId, sourceLoc.side);
        }

        // 4. Match -> Match Group Header (Move room to group)
        else if (
            sourceLoc.type === 'match' &&
            typeof targetId === 'string' &&
            targetId.startsWith('match-group-')
        ) {
            const matchId = Number(targetId.replace('match-group-', ''));
            if (sourceLoc.match.id === matchId) return; // Same group

            const targetMatch = matches.find(m => m.id === matchId);
            if (targetMatch) {
                addToMatch(targetMatch, sourceId, sourceLoc.side);
            }
        }
    };

    // Sort room matches by the minimum insurer lineItemNumber across all items in the room
    const sortedMatches = [...matches].sort((a, b) => {
        const allInsurerA = a.insurerDetails?.flatMap(d => d.items) || [];
        const allInsurerB = b.insurerDetails?.flatMap(d => d.items) || [];
        const minA = allInsurerA.length > 0
            ? Math.min(...allInsurerA.map(i => parseLineItemNum(i.lineItemNumber)))
            : Infinity;
        const minB = allInsurerB.length > 0
            ? Math.min(...allInsurerB.map(i => parseLineItemNum(i.lineItemNumber)))
            : Infinity;
        if (minA !== minB) return minA - minB;
        // Fallback to alphabetical room name
        const nameA = a.roomA?.[0] || a.roomB?.[0] || '';
        const nameB = b.roomA?.[0] || b.roomB?.[0] || '';
        return nameA.localeCompare(nameB);
    });

    // Filter matches based on search query AND search terms (OR logic)
    const activeTerms = [filters.searchQuery, ...filters.searchTerms].filter(t => t.trim() !== '').map(t => t.toLowerCase());

    const filterFn = (text: string) => {
        if (activeTerms.length === 0) return true;
        return activeTerms.some(term => text.toLowerCase().includes(term));
    };

    const filteredMatches = sortedMatches.filter(match => {
        // Apply confidence filter
        const conf = match.confidence || 0;
        if (filters.confidenceLevel === 'high' && conf < 0.8) return false;
        if (filters.confidenceLevel === 'medium' && (conf < 0.5 || conf >= 0.8)) return false;
        if (filters.confidenceLevel === 'low' && conf >= 0.5) return false;

        // Apply "Show Matched" logic:
        // If showMatched is TRUE (default), show EVERYTHING (Pending + Approved).
        // If showMatched is FALSE, HIDE Approved matches (Show Pending only).
        if (!filters.showMatched && match.review?.status === 'approved') return false;

        // Apply search filter
        const roomAName = match.roomA?.join(', ') || '';
        const roomBName = match.roomB?.join(', ') || '';
        const matchText = `${roomAName} ${roomBName}`;
        return filterFn(matchText);
    });

    // Filter Unmatched
    const filteredInsurerUnmatched = unmatchedInsurerRooms.filter(r => {
        const text = r.level ? `${r.level} - ${r.name}` : r.name;
        return filterFn(text);
    });
    const filteredContractorUnmatched = unmatchedContractorRooms.filter(r => {
        const text = r.level ? `${r.level} - ${r.name}` : r.name;
        return filterFn(text);
    });

    const hasUnmatched = filteredInsurerUnmatched.length > 0 || filteredContractorUnmatched.length > 0;

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <div className="space-y-8">
                {/* Matches Section - Collapsible Card */}
                <Card className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 shadow-sm">
                    <Collapsible open={isMatchesOpen} onOpenChange={setIsMatchesOpen}>
                        <div
                            className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50/50 dark:hover:bg-slate-900/50 rounded-t-lg transition-colors"
                            onClick={() => setIsMatchesOpen(!isMatchesOpen)}
                        >
                            <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
                                Room Matches
                                <Badge variant="secondary" className="text-xs font-normal text-muted-foreground ml-1">
                                    {filteredMatches.length}
                                </Badge>
                            </h3>
                            <CollapsibleTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-primary">
                                    <ChevronRight className={cn("h-5 w-5 transition-transform duration-200", isMatchesOpen && "rotate-90")} />
                                </Button>
                            </CollapsibleTrigger>
                        </div>

                        <CollapsibleContent>
                            <div className="p-4 pt-0 border-t border-slate-100 dark:border-slate-800/50 mt-1">
                                <div className="pt-4 grid gap-4">
                                    {filteredMatches.map((match, idx) => (
                                        <RoomMatchCard
                                            key={match.id}
                                            match={match}
                                            index={idx}
                                            onApprove={() => onApprove(match.id)}
                                            onReject={() => onReject(match.id)}
                                            onUnmatchRoom={(roomId, side) => handleUnmatchRoom(match, roomId, side)}
                                            onRematchRoom={() => onRematchRoom(match.id)}
                                        />
                                    ))}
                                    {filteredMatches.length === 0 && (
                                        <div className="text-center py-12 text-muted-foreground bg-muted/30 rounded-xl border border-dashed">
                                            No matches found matching your filters.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </CollapsibleContent>
                    </Collapsible>
                </Card>

                {/* Unmatched Rooms Section - Collapsible Card */}
                {hasUnmatched && (
                    <UnmatchedRoomsSection
                        insurerRooms={filteredInsurerUnmatched}
                        contractorRooms={filteredContractorUnmatched}
                        matches={matches}
                        selectedRoomIds={selectedRoomIds}
                        onToggleSelection={onToggleRoomSelection}
                        onClearSelection={onClearRoomSelection}
                        onMoveRooms={handleBulkMove}
                    />
                )}
            </div>
            <DragOverlay>
                {activeId ? (
                    <div className="p-2 bg-white dark:bg-slate-800 rounded border shadow opacity-80 w-64 truncate">
                        {(() => {
                            const loc = findRoomLocation(activeId);
                            return loc ? loc.room.name : "Moving Room...";
                        })()}
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};

interface UnmatchedRoomsSectionProps {
    insurerRooms: OrganizedRoom[];
    contractorRooms: OrganizedRoom[];
    matches?: EnhancedRoomMatch[];
    selectedRoomIds?: Set<number>;
    onToggleSelection?: (id: number) => void;
    onClearSelection?: () => void;
    onMoveRooms?: (targetMatchId: number | 'new', roomIds: number[]) => void;
}

function UnmatchedRoomsSection({
    insurerRooms,
    contractorRooms,
    matches = [],
    selectedRoomIds = new Set(),
    onToggleSelection,
    onClearSelection,
    onMoveRooms
}: UnmatchedRoomsSectionProps) {
    const [isOpen, setIsOpen] = useState(true);
    const { setNodeRef } = useDroppable({ id: 'unmatched-zone' });

    // Filter selection to only those currently in these lists
    const validSelectedIds = Array.from(selectedRoomIds).filter(id =>
        insurerRooms.some(r => r.id === id) || contractorRooms.some(r => r.id === id)
    );
    const hasSelection = validSelectedIds.length > 0;
    const [targetMatchId, setTargetMatchId] = useState<string>('new');

    const handleMove = () => {
        if (!onMoveRooms) return;
        const target = targetMatchId === 'new' ? 'new' : Number(targetMatchId);
        onMoveRooms(target, validSelectedIds);
        // Clear selection is typically handled by parent after move, or we can here
    };

    return (
        <div className="space-y-4 pt-4 border-t" ref={setNodeRef}>
            <Card className="bg-slate-50/50 dark:bg-slate-950/50 border-slate-200 dark:border-slate-800">
                <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                    <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-100/50 dark:hover:bg-slate-800/50 rounded-t-lg" onClick={() => setIsOpen(!isOpen)}>
                        <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
                            Unmatched Rooms
                            <Badge variant="secondary" className="text-xs font-normal text-muted-foreground">{insurerRooms.length + contractorRooms.length}</Badge>
                        </h3>
                        <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-primary">
                                <ChevronRight className={cn("h-5 w-5 transition-transform duration-200", isOpen && "rotate-90")} />
                            </Button>
                        </CollapsibleTrigger>
                    </div>

                    <CollapsibleContent>
                        {/* Bulk Action Bar */}
                        {hasSelection && (
                            <div className="bg-primary/5 border-b border-primary/10 px-4 py-2 flex flex-col sm:flex-row items-center gap-4 animate-in slide-in-from-top-1" onClick={e => e.stopPropagation()}>
                                <div className="flex items-center gap-2 w-full sm:w-auto">
                                    <Badge variant="default" className="bg-primary text-primary-foreground">
                                        {validSelectedIds.length} Selected
                                    </Badge>
                                    <Button variant="ghost" size="sm" className="h-6 text-xs text-muted-foreground hover:text-foreground px-2" onClick={(e) => { e.stopPropagation(); onClearSelection?.(); }}>
                                        Clear
                                    </Button>
                                </div>
                                <div className="flex-1 hidden sm:block" />
                                <div className="flex items-center gap-2 w-full sm:w-auto">
                                    <span className="text-xs text-muted-foreground whitespace-nowrap hidden sm:inline">Move to:</span>
                                    <Select value={targetMatchId} onValueChange={setTargetMatchId}>
                                        <SelectTrigger className="w-full sm:w-[320px] h-8 text-xs bg-background">
                                            <SelectValue placeholder="Select Match Group" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="new">✨ New Match Group</SelectItem>
                                            {matches.map(m => (
                                                <SelectItem key={m.id} value={m.id.toString()}>
                                                    <span className="font-mono text-muted-foreground mr-2 opacity-50">#{m.id}</span>
                                                    <span className="font-medium">{m.roomA?.join(', ') || 'None'}</span>
                                                    <span className="mx-1 text-muted-foreground">↔</span>
                                                    <span className="font-medium text-blue-600 dark:text-blue-400">{m.roomB?.join(', ') || 'None'}</span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button size="sm" className="h-8" onClick={(e) => { e.stopPropagation(); handleMove(); }}>
                                        Move
                                    </Button>
                                </div>
                            </div>
                        )}

                        <div className="p-4 pt-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Insurer Unmatched */}
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between text-xs text-muted-foreground uppercase tracking-wider">
                                        <span>Insurer Only ({insurerRooms.length})</span>
                                    </div>
                                    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3 space-y-2 max-h-[400px] overflow-y-auto border border-dashed border-slate-200 dark:border-slate-800">
                                        {insurerRooms.map(room => (
                                            <DraggableRoomCard
                                                key={room.id}
                                                room={room}
                                                type="insurer"
                                                items={room.items}
                                                isSelected={selectedRoomIds.has(room.id)}
                                                onToggleSelection={() => onToggleSelection?.(room.id)}
                                            />
                                        ))}
                                        {insurerRooms.length === 0 && <span className="text-sm italic text-muted-foreground">None found</span>}
                                    </div>
                                </div>

                                {/* Contractor Unmatched */}
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between text-xs text-muted-foreground uppercase tracking-wider">
                                        <span>Contractor Only ({contractorRooms.length})</span>
                                    </div>
                                    <div className="bg-blue-50/50 dark:bg-blue-900/20 rounded-lg p-3 space-y-2 max-h-[400px] overflow-y-auto border border-dashed border-blue-200 dark:border-blue-800/50">
                                        {contractorRooms.map(room => (
                                            <DraggableRoomCard
                                                key={room.id}
                                                room={room}
                                                type="contractor"
                                                items={room.items}
                                                isSelected={selectedRoomIds.has(room.id)}
                                                onToggleSelection={() => onToggleSelection?.(room.id)}
                                            />
                                        ))}
                                        {contractorRooms.length === 0 && <span className="text-sm italic text-muted-foreground">None found</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CollapsibleContent>
                </Collapsible>
            </Card>
        </div>
    );
}

// --- Subcomponents ---

interface DraggableRoomCardProps {
    room: OrganizedRoom;
    type: 'insurer' | 'contractor';
    items: RoomMatchDetails['items'];
    onRemove?: () => void;
    isSelected?: boolean;
    onToggleSelection?: () => void;
}

function DraggableRoomCard({ room, type, items, onRemove, isSelected, onToggleSelection }: DraggableRoomCardProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isOver,
    } = useSortable({ id: room.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const [isOpen, setIsOpen] = useState(false);
    const hasItems = items && items.length > 0;

    // Sort items by lineItemNumber for consistent display order
    const sortedItems = hasItems
        ? [...items].sort(compareLineItems)
        : items;

    return (
        <Card className={cn(
            "group bg-white dark:bg-slate-950 transition-all w-full border relative",
            type === 'insurer' ? "border-slate-200" : "border-blue-200 dark:border-blue-900",
            isOver && "ring-2 ring-primary ring-offset-2 bg-primary/5 z-10 scale-[1.01]"
        )}
            ref={setNodeRef}
            style={style}
        >
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                <div className="flex items-center gap-2 p-3 pr-8">
                    {/* Checkbox (if enabled) */}
                    {onToggleSelection && (
                        <div className="mr-1">
                            <Checkbox
                                id={`select-room-${room.id}`}
                                checked={isSelected || false}
                                onCheckedChange={() => onToggleSelection()}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    )}

                    {/* Drag Handle */}
                    <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded">
                        <GripVertical className="h-4 w-4 text-muted-foreground/30" />
                    </div>

                    {/* Expand Toggle */}
                    <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-primary disabled:opacity-0" disabled={!hasItems}>
                            <ChevronRight className={cn("h-4 w-4 transition-transform duration-200", isOpen && "rotate-90")} />
                        </Button>
                    </CollapsibleTrigger>

                    {/* Room Info */}
                    <div className="flex-1 min-w-0 flex items-center justify-between gap-4">
                        <div className="font-medium text-sm flex-1">
                            {room.level ? `${room.level} - ${room.name}` : room.name}
                        </div>
                        <div className="text-sm font-medium text-foreground whitespace-nowrap">
                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(room.totalRCV)}
                        </div>
                    </div>
                </div>

                {/* Remove Button (Absolute positioned) */}
                {onRemove && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2 h-6 w-6 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove();
                        }}
                    >
                        <X className="h-3 w-3" />
                    </Button>
                )}

                <CollapsibleContent>
                    <div className="px-3 pb-3 pt-0 space-y-2">
                        <div className={cn(
                            "rounded-md border p-2 text-xs space-y-2",
                            type === 'insurer'
                                ? "bg-slate-50 border-slate-100 dark:bg-slate-900 dark:border-slate-800"
                                : "bg-blue-50/30 border-blue-100 dark:bg-blue-900/20 dark:border-blue-800"
                        )}>
                            <div className="font-semibold text-muted-foreground px-1">Line Items</div>
                            {sortedItems.map((item, i) => (
                                <div key={i} className="flex justify-between items-start gap-4 p-1.5 bg-card rounded border border-transparent hover:border-border transition-colors">
                                    <div className="space-y-0.5">
                                        <div className="font-medium line-clamp-2">{item.description}</div>
                                        <div className="text-[10px] text-muted-foreground">
                                            {item.lineItemNumber && <span className="mr-1">#{item.lineItemNumber}</span>}
                                            {item.qty || 0} {item.uom || 'EA'} @ {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((item.unitPriceCents || 0) / 100)}
                                        </div>
                                    </div>
                                    <div className="font-medium text-muted-foreground">
                                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((item.rcvCents || 0) / 100)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </Card>
    );
}

interface RoomMatchCardProps {
    match: EnhancedRoomMatch;
    index: number;
    onApprove: () => void;
    onReject: () => void;
    onUnmatchRoom: (roomId: number, side: 'insurer' | 'contractor') => void;
    onRematchRoom: () => void;
}

function RoomMatchCard({ match, onApprove, onReject, onUnmatchRoom, onRematchRoom }: RoomMatchCardProps) {
    const [isOpen, setIsOpen] = useState(false);

    // Droppable ID with prefix
    const roomMatchGroupId = `match-group-${match.id}`;
    const { setNodeRef } = useDroppable({ id: roomMatchGroupId });

    // Custom monitor for refined highlighting (works when hovering children)
    const [isTargeted, setIsTargeted] = useState(false);

    useDndMonitor({
        onDragOver({ over }) {
            if (!over) {
                if (isTargeted) setIsTargeted(false);
                return;
            }

            // 1. Direct hover on the card
            if (over.id === roomMatchGroupId) {
                if (!isTargeted) setIsTargeted(true);
                return;
            }

            // 2. Hover on a child room
            const oid = Number(over.id);
            if (!isNaN(oid)) {
                // Check if this room belongs to this match group
                const hasRoom = match.insurerDetails?.some(r => r.room.id === oid) ||
                    match.contractorDetails?.some(r => r.room.id === oid);
                if (hasRoom) {
                    if (!isTargeted) setIsTargeted(true);
                    return;
                }
            }

            if (isTargeted) setIsTargeted(false);
        },
        onDragEnd() {
            setIsTargeted(false);
        }
    });

    // Confidence Color Logic
    const confidencePct = match.confidence * 100;
    let badgeColor = "bg-red-500 hover:bg-red-600";
    if (confidencePct >= 100) badgeColor = "bg-green-600 hover:bg-green-700";
    else if (confidencePct >= 90) badgeColor = "bg-green-400 hover:bg-green-500 text-green-950";
    else if (confidencePct >= 70) badgeColor = "bg-yellow-500 hover:bg-yellow-600 text-yellow-950";

    const isApproved = match.review?.status === 'approved';

    // Safety check for undefined arrays
    const insurerDetails = match.insurerDetails || [];
    const contractorDetails = match.contractorDetails || [];

    const iTotal = insurerDetails.reduce((s, r) => s + r.total, 0);
    const cTotal = contractorDetails.reduce((s, r) => s + r.total, 0);
    const diff = cTotal - iTotal;

    const insurerNames = match.roomA?.join(', ') || 'None';
    const contractorNames = match.roomB?.join(', ') || 'None';

    return (
        <Card
            className={cn(
                "overflow-hidden border-l-4 transition-all",
                isTargeted
                    ? "border-l-primary ring-2 ring-primary ring-offset-2 bg-primary/5 scale-[1.01] shadow-lg"
                    : "border-l-slate-200 dark:border-l-slate-800 hover:border-l-primary"
            )}
            ref={setNodeRef}
        >
            <CardContent className="p-0">
                <Collapsible open={isOpen} onOpenChange={setIsOpen}>

                    {/* Header Row - Single Line Layout */}
                    <div className="flex flex-col lg:flex-row lg:items-center items-start p-3 gap-4 min-h-[60px]">

                        {/* Toggle, Approved Status, & Match Badge */}
                        <div className="flex items-center gap-3 w-full lg:w-auto shrink-0">
                            <CollapsibleTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-primary shrink-0">
                                    <ChevronRight className={cn("h-5 w-5 transition-transform duration-200", isOpen && "rotate-90")} />
                                </Button>
                            </CollapsibleTrigger>

                            {isApproved && (
                                <Badge className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-0.5 whitespace-nowrap">
                                    Approved
                                </Badge>
                            )}

                            <Badge className={cn("text-xs font-semibold px-2 py-1 whitespace-nowrap text-white border-0", badgeColor)}>
                                {confidencePct.toFixed(0)}% Match
                            </Badge>
                        </div>

                        {/* Room Summary - Left Aligned & Flexible */}
                        <div className="flex-1 flex flex-wrap items-center justify-start gap-2 text-sm w-full font-medium">
                            <div className="text-foreground break-words leading-tight">{insurerNames}</div>
                            <div className="shrink-0 text-muted-foreground">↔</div>
                            <div className="text-blue-700 dark:text-blue-400 break-words leading-tight">{contractorNames}</div>
                        </div>

                        {/* Variance & Actions section */}
                        <div className="flex items-center gap-6 justify-between w-full lg:w-auto mt-2 lg:mt-0 pt-2 lg:pt-0 border-t lg:border-t-0 border-border">
                            {/* Variance */}
                            <div className="flex items-center gap-2 whitespace-nowrap">
                                <span className="text-xs text-muted-foreground uppercase tracking-wider">Total:</span>
                                <span className={cn("text-sm", diff > 0 ? "text-orange-600" : "text-green-600")}>
                                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(diff)}
                                </span>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2">
                                <Button size="sm" variant="outline" className="h-8 px-3 gap-2" onClick={(e) => { e.stopPropagation(); onRematchRoom(); }}>
                                    <RefreshCw className="h-3.5 w-3.5" />
                                    Rematch
                                </Button>
                                <Button size="sm" variant="outline" className="h-8 px-3 text-muted-foreground hover:text-destructive hover:border-destructive" onClick={(e) => { e.stopPropagation(); onReject(); }}>
                                    Reject
                                </Button>
                                {!isApproved && (
                                    <Button size="sm" className="h-8 px-3 gap-2" onClick={(e) => { e.stopPropagation(); onApprove(); }}>
                                        Approve
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Reasoning / Notes */}
                    {match.reasoning && (
                        <div className="px-14 pb-3 -mt-1">
                            <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Reasoning</div>
                            <div className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                                {isOpen
                                    ? match.reasoning
                                    : (match.reasoning.length > 120
                                        ? `${match.reasoning.substring(0, 120)}...`
                                        : match.reasoning)
                                }
                            </div>
                        </div>
                    )}

                    {/* Expandable Content - Columns of Rooms */}
                    <CollapsibleContent>
                        <div className="border-t border-border bg-slate-50/50 dark:bg-slate-900/10 p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Insurer Side */}
                                <div className="space-y-3">
                                    <div className="text-[10px] text-muted-foreground mb-1 uppercase tracking-wider">Insurance Rooms</div>
                                    <div className="space-y-3">
                                        {insurerDetails.map((details) => (
                                            <DraggableRoomCard
                                                key={`insurer-${details.room.id}`} // Use room.id for key
                                                room={{
                                                    id: details.room.id,
                                                    name: details.room.name,
                                                    items: details.items,
                                                    totalRCV: details.total,
                                                    type: 'insurer',
                                                    matchId: match.id
                                                }}
                                                type="insurer"
                                                items={details.items}
                                                onRemove={() => onUnmatchRoom(details.room.id, 'insurer')}
                                            />
                                        ))}
                                        {insurerDetails.length === 0 && <div className="text-sm text-muted-foreground italic pl-2">No insurer rooms</div>}
                                    </div>
                                </div>

                                {/* Contractor Side */}
                                <div className="space-y-3">
                                    <div className="text-[10px] text-muted-foreground mb-1 uppercase tracking-wider text-right">Contractor Rooms</div>
                                    <div className="space-y-3">
                                        {contractorDetails.map((details) => (
                                            <DraggableRoomCard
                                                key={`contractor-${details.room.id}`} // Use room.id for key
                                                room={{
                                                    id: details.room.id,
                                                    name: details.room.name,
                                                    items: details.items,
                                                    totalRCV: details.total,
                                                    type: 'contractor',
                                                    matchId: match.id
                                                }}
                                                type="contractor"
                                                items={details.items}
                                                onRemove={() => onUnmatchRoom(details.room.id, 'contractor')}
                                            />
                                        ))}
                                        {contractorDetails.length === 0 && <div className="text-sm text-muted-foreground italic pl-2 text-right">No contractor rooms</div>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CollapsibleContent>

                </Collapsible>
            </CardContent>
        </Card>
    );
}
